import clsx from 'clsx';
import React, { ComponentPropsWithoutRef, ReactNode, useCallback, useMemo } from 'react';
import { PlayIcon } from '../../../../styled/Icon';
import FadeInDotIndicator from '../../../../components-legacy/ImageSlider/FadeInDotIndicator';
import { Dot, DotIndicator } from '../../../../styled/DotIndicator/wrappers';

import {
  Thumbnail,
  ThumbnailImage,
  ThumbnailList,
  ThumbnailIcon,
} from '../../../../styled/Thumbnail/wrappers';

interface IThumbnailWrapper {
  children: ReactNode;
  index: number;
  onClick: (index: number) => void;
}

const ThumbnailWrapper = ({
  children,
  index,
  onClick,
  ...props
}: IThumbnailWrapper & Omit<ComponentPropsWithoutRef<typeof Thumbnail>, 'onClick'>) => {
  const click = useCallback(() => {
    onClick(index);
  }, [onClick, index]);

  return (
    <Thumbnail onClick={click} {...props}>
      {children}
    </Thumbnail>
  );
};

interface IImageSliderControls {
  imageCount: number;
  index: number;
  onIndexChange: (index: number) => void;
  srcs: {
    src: string;
    type: string;
    alt?: string;
    sources?: {
      srcSet: string;
      sizes?: string;
      type?: string;
      media?: string;
    }[];
  }[];
}

const ImageSliderControls = ({ imageCount, index, onIndexChange, srcs }: IImageSliderControls) => {
  // Creates an array from the number of images in the slider. Used to map and render dotindicator.
  const steps = useMemo(() => Array.from(Array(imageCount).keys()), [imageCount]);

  return (
    <>
      {!!steps?.length && steps?.length > 7 && (
        <FadeInDotIndicator images={steps} activeStep={index} />
      )}
      {!!steps?.length && steps?.length <= 7 && (
        <DotIndicator className="hidden-md">
          {steps.map((_, i) => (
            <Dot key={i} className={i === index ? 'active' : undefined} />
          ))}
        </DotIndicator>
      )}
      {(srcs?.length || 0) > 1 && (
        <ThumbnailList className="hidden-lt-md">
          {srcs.map(({ src, sources, type, alt }, i) => (
            <ThumbnailWrapper
              key={`${src}-${i}`}
              className={clsx(i === index && 'active')}
              onClick={onIndexChange}
              index={i}>
              <picture>
                {sources?.map((source) => (
                  <source key={source.srcSet} type={source.type} srcSet={source.srcSet} />
                ))}
                <ThumbnailImage src={src} alt={alt} loading="lazy" />
              </picture>
              {type === 'video' && (
                <ThumbnailIcon>
                  <PlayIcon className="icon" />
                </ThumbnailIcon>
              )}
            </ThumbnailWrapper>
          ))}
        </ThumbnailList>
      )}
    </>
  );
};

export default ImageSliderControls;
